// Common ESA Bus — host implementation (§3.5, §7.2 / DR-17).
//
// ONE bi-directional channel between any Surface (workspace, canvas, embedded
// Superset) and the omnipresent ESA panel. Because the channel is identical
// across Surface types, ESA's context-carry and instruction logic is written
// once here (O(1)) — adding a new Surface type means implementing the contract,
// not extending ESA.
//
// Exposed across the Module Federation boundary so remotes get the same bus.

import type {
  ESABus,
  ESAInstruction,
  RefreshScope,
  SurfaceSelectionContext,
  SurfaceType,
  Unsubscribe,
} from '@growthaxl/web-foundation';
import { shellEventBus } from '../../shell/events/eventBus';

type InstructionHandler = (instruction: ESAInstruction) => void;
type RefreshHandler = (scope: RefreshScope) => void;

interface SurfaceChannel {
  instructionHandlers: Set<InstructionHandler>;
  refreshHandlers: Set<RefreshHandler>;
}

class ESABusManager {
  private channels = new Map<string, SurfaceChannel>();
  /** Latest selection context per surface — the ESA panel reads this for context carry. */
  private latestContext = new Map<string, SurfaceSelectionContext>();
  private contextListeners = new Set<(ctx: SurfaceSelectionContext) => void>();

  private channel(surfaceId: string): SurfaceChannel {
    let ch = this.channels.get(surfaceId);
    if (!ch) {
      ch = { instructionHandlers: new Set(), refreshHandlers: new Set() };
      this.channels.set(surfaceId, ch);
    }
    return ch;
  }

  /** Create the ESABus a Surface consumes via useESABus(). */
  createBus(surfaceId: string, surfaceType: SurfaceType): ESABus {
    return {
      sendContext: (context: SurfaceSelectionContext) => {
        const ctx = { ...context, surfaceId, surfaceType };
        this.latestContext.set(surfaceId, ctx);
        this.contextListeners.forEach(l => l(ctx));
        shellEventBus.dispatch({
          type: 'ESA_SURFACE_SELECTION_CHANGED',
          payload: {
            surfaceId: ctx.surfaceId,
            surfaceType: ctx.surfaceType,
            selection: ctx.selection,
          },
        });
      },
      sendMessage: (message: string) => {
        shellEventBus.dispatch({ type: 'ESA_SEND_MESSAGE', payload: { message } });
      },
      onInstruction: (handler: InstructionHandler): Unsubscribe => {
        const ch = this.channel(surfaceId);
        ch.instructionHandlers.add(handler);
        return () => ch.instructionHandlers.delete(handler);
      },
      onRefresh: (handler: RefreshHandler): Unsubscribe => {
        const ch = this.channel(surfaceId);
        ch.refreshHandlers.add(handler);
        return () => ch.refreshHandlers.delete(handler);
      },
    };
  }

  // ----- ESA panel side: push instructions/refresh down to a Surface -----

  pushInstruction(surfaceId: string, instruction: ESAInstruction): void {
    this.channel(surfaceId).instructionHandlers.forEach(h => h(instruction));
  }

  pushRefresh(surfaceId: string, scope: RefreshScope = {}): void {
    this.channel(surfaceId).refreshHandlers.forEach(h => h(scope));
  }

  getLatestContext(surfaceId: string): SurfaceSelectionContext | undefined {
    return this.latestContext.get(surfaceId);
  }

  onContext(listener: (ctx: SurfaceSelectionContext) => void): Unsubscribe {
    this.contextListeners.add(listener);
    return () => this.contextListeners.delete(listener);
  }
}

export const esaBusManager = new ESABusManager();

/**
 * Surface Interface Contract entry point — §7.2 useESABus(). Returns a bus
 * scoped to the given Surface. Workspaces/canvases call this with their own id.
 */
export function useESABus(surfaceId: string, surfaceType: SurfaceType = 'workspace'): ESABus {
  return esaBusManager.createBus(surfaceId, surfaceType);
}
